import { useEffect, useEffectEvent, useRef } from 'react';
import {
  BlindKingsGame,
  GAME_MODES,
  GAME_STATUSES,
} from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import { pickBotAction, preloadBotModel } from '../../../bot/onnxPolicy';
import type { GameRecord } from './use-game-state';

type Options = {
  game: GameRecord;
  playerId?: string;
  /** True while a human optimistic animation is in flight. */
  inputLocked: boolean;
  applyGameUpdate?: (payload: GameRecord) => void;
  onError?: (message: string) => void;
};

/**
 * When it's Blind Bot's turn in an AI game, run ONNX + dispatchBot.
 */
export default function useBotOpponent({
  game,
  playerId,
  inputLocked,
  applyGameUpdate,
  onError,
}: Options) {
  const busyRef = useRef(false);
  const gameRef = useRef(game);
  gameRef.current = game;

  const applyUpdate = useEffectEvent((payload: GameRecord) => {
    applyGameUpdate?.(payload);
  });
  const reportError = useEffectEvent((message: string) => {
    onError?.(message);
  });

  const gameMode = String(game.gameMode ?? '');
  const status = String(game.status ?? '');
  const difficulty = String(game.botDifficulty ?? 'hard');
  const encoded = String(game.encodedGameState ?? '');
  const updatedAt = Number(game.updatedAt ?? 0);
  const gameId = String(game.gameId ?? '');

  useEffect(() => {
    if (gameMode !== GAME_MODES.AI_GAME) return;
    preloadBotModel();
  }, [gameMode]);

  useEffect(() => {
    if (gameMode !== GAME_MODES.AI_GAME) return;
    if (status !== GAME_STATUSES.STARTED) return;
    if (!playerId || !encoded || !gameId) return;
    if (inputLocked) return;
    if (busyRef.current) return;

    const snapshot = gameRef.current;
    const bk = new BlindKingsGame({
      ...(snapshot as Record<string, unknown>),
      userPlayerId: playerId,
    });
    if (bk.isGameOver || bk.gameWinner) return;
    if (bk.isUserTurn) return;

    let cancelled = false;
    busyRef.current = true;
    const startedFromUpdatedAt = Number(snapshot.updatedAt ?? 0);

    (async () => {
      try {
        await new Promise((r) => setTimeout(r, 350));
        if (cancelled) return;

        const latest = gameRef.current;
        // Another update already advanced the game past what we planned to answer.
        if (Number(latest.updatedAt ?? 0) !== startedFromUpdatedAt) return;

        const botView = new BlindKingsGame({
          ...(latest as Record<string, unknown>),
          userPlayerId: playerId,
        });
        if (botView.isUserTurn || botView.isGameOver) return;

        const payload = await pickBotAction(
          botView,
          String(latest.botDifficulty ?? difficulty)
        );
        if (cancelled) return;
        if (Number(gameRef.current.updatedAt ?? 0) !== startedFromUpdatedAt) {
          return;
        }

        const result = (await api.dispatchBot(gameId, payload)) as GameRecord;
        // Always merge a successful bot move — even if this effect was cleaned up —
        // otherwise the server advances and the UI stays stale until refresh.
        applyUpdate(result);
      } catch (err) {
        console.error('[bot]', err);
        reportError(err instanceof Error ? err.message : String(err));
      } finally {
        busyRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    gameMode,
    status,
    difficulty,
    encoded,
    updatedAt,
    gameId,
    playerId,
    inputLocked,
  ]);
}
